'use client';

import React, { useMemo } from 'react';
import { Grid, GridItem, useBreakpointValue } from '@chakra-ui/react';
import type { Product } from '@/models/product/product';
import { useProductService } from '@/hooks/product/useProductService';
import { useReviewService } from '@/hooks/review/useReviewService';
import { type ProductCategory } from '@/constants/product/category';
import { getLowestPriceProductBasicInfo, normalizeCategory } from '@/utils/domain/product';
import ProductCard from '@/components/products/ProductCard';
import { createReviewStatsMap } from '@/utils/domain/review';
import { useSafeRouter } from '@/hooks/route/useSafeRouter';

interface TopRankedProductGridProps {
  selectedCategory: ProductCategory;
}

const TopRankedProductGrid = ({ selectedCategory }: TopRankedProductGridProps) => {
  const { useGetTopRankedProducts } = useProductService();
  const { useGetReviewStatistics } = useReviewService();
  const { data: productsData } = useGetTopRankedProducts({
    category: selectedCategory,
  });

  const { push } = useSafeRouter();

  const productLimit = useBreakpointValue({
    base: 5,
    tablet: 6,
    desktop: 9,
  });

  const filteredProducts: Product[] = useMemo(
    () =>
      productsData?.products
        .filter((product: Product) => product.category.some((cat) => normalizeCategory(cat) === selectedCategory))
        .slice(0, productLimit) || [],
    [productsData, selectedCategory, productLimit],
  );

  const productIds = useMemo(() => {
    if (filteredProducts.length === 0) {
      return '';
    }
    return filteredProducts.map((product) => product.productId).join(',');
  }, [filteredProducts]);

  const { data: reviewStats } = useGetReviewStatistics({ productIds });

  const reviewStatsMap = useMemo(() => createReviewStatsMap(reviewStats), [reviewStats]);

  return (
    <Grid
      templateColumns={{
        base: 'repeat(1, 1fr)',
        tablet: 'repeat(2, 1fr)',
        desktop: 'repeat(3, 1fr)',
      }}
      base={{
        '& > *:nth-of-type(n+6)': {
          display: 'none',
        },
      }}
      tablet={{
        '& > *:nth-of-type(n+7)': {
          display: 'none',
        },
      }}
      desktop={{
        '& > *:nth-of-type(n+1)': {
          display: 'initial',
        },
      }}
      gap={{ base: 3, tablet: 4 }}
      width="100%"
    >
      {filteredProducts.map((product, index) => {
        const productInfo = getLowestPriceProductBasicInfo(product);
        const productReviewStats = reviewStatsMap[product.productId];
        return (
          <GridItem key={productInfo.productId}>
            <ProductCard
              onClick={() => {
                push(productInfo.productLink);
              }}
              orientation="horizontal"
              showRanking
              ranking={index + 1}
              showRating
              name={productInfo.name}
              brand={productInfo.brand}
              productImage={productInfo.productImageUrls[0]}
              sku={productInfo.sku}
              currency={productInfo.currency}
              originalPrice={productInfo.originalPrice}
              salePrice={productInfo.salePrice}
              rating={productReviewStats?.averageRate}
              reviewCount={productReviewStats?.totalReviews}
            />
          </GridItem>
        );
      })}
    </Grid>
  );
};

export default TopRankedProductGrid;
